import { z } from 'zod';
// Autopilot weekly recap (the autopilot roadmap (CommitMind) A8b).
//
// Every Friday 4pm brand-tz, the recap scheduler rolls up the
// past 7 days of autopilot_audit_log entries for a brand and
// lands one of these cards. Founder reads what mag did + acks /
// dismisses. The ack/dismiss feeds trust-ledger graduation
// prompts for the autopilot decision types (a streak of
// acked recaps signals "you can trust mag more").
//
// Card lands status='open' so the founder explicitly decides.
// Per-brand-per-week dedup lives in the runner via weekStart.
/** Count-by-key rollup. Stored as a record so adding new
 *  action kinds doesn't churn the schema. */
const recapCountsSchema = z.record(z.string(), z.number().int().min(0));
/** Highlight entry — a notable action surfaced inline on the card
 *  so the founder can see the most consequential things mag did
 *  without expanding to the full audit log. */
const recapHighlightSchema = z.object({
    /** Mag-voice one-liner describing the action + its receipts.
     *  ("paused 'why your funnel breaks at step 3' — ctr 0.4% over
     *  4 days at $11.20 spend.") */
    line: z.string().min(1).max(280),
    /** ISO timestamp the action happened. Surfaces as relative ago. */
    at: z.string().datetime(),
    /** The action_kind value from the audit log row. Useful for
     *  the renderer to color/icon-style the highlight. */
    actionKind: z.string().min(1).max(32),
});
/** s236-4d/5 — per-bundle scope-gate-skip rollup. When mag tried
 *  to act on a channel but the brand's meta connection didn't
 *  have the required scope, the scheduler writes a scope_gate_skip
 *  audit row. The recap aggregates these into one "blocker"
 *  entry per affected channel/bundle so the founder sees
 *  "i couldn't do X" surfaced alongside "i did Y". */
const recapBlockerSchema = z.object({
    /** Which bundle the founder needs to grant to unblock. Drives
     *  the extend URL the renderer builds. */
    bundle: z.enum(['meta_organic', 'meta_paid']),
    /** Action names that were blocked (publish, paid_launch,
     *  paid_pause, ...). Deduped from the underlying audit rows. */
    blockedActions: z.array(z.string()).min(1).max(8),
    /** Total scope_gate_skip rows for this bundle in the window.
     *  Surfaces as "i couldn't do N things". */
    count: z.number().int().min(1),
    /** Specific scope names missing across the blocked rows.
     *  Renderer can show "missing: ads_management, ads_read".
     *  Deduped + capped. */
    missingScopes: z.array(z.string()).min(1).max(8),
});
export const autopilotWeeklyRecapPayloadSchema = z.object({
    kind: z.literal('autopilot_weekly_recap'),
    /** YYYY-MM-DD in brand tz — the Monday that starts the window.
     *  Per-brand-per-week dedup key. */
    weekStart: z.string(),
    /** YYYY-MM-DD in brand tz — the Sunday that ends the window
     *  (inclusive). 7 days from weekStart. */
    weekEnd: z.string(),
    /** Total autonomous actions mag took in the window. */
    totalActions: z.number().int().min(0),
    /** Per-action-kind count. Keys are action_kind values:
     *  publish, skip, reschedule, rewrite, paid_launch,
     *  paid_pause, paid_unpause. */
    byKind: recapCountsSchema,
    /** Per-channel count. Keys: meta_organic, meta_paid, google_paid.
     *  Sums to totalActions across channels. */
    byChannel: recapCountsSchema,
    /** Up to 5 notable actions, in mag's voice, with timestamps.
     *  Renderer surfaces these inline; the full audit log is
     *  available via the per-campaign endpoint. */
    highlights: z.array(recapHighlightSchema).max(5),
    /** s236-4d/5 — per-bundle scope blockers. Omitted/empty when no
     *  scope_gate_skip rows in the window. Renderer shows each as
     *  a "i couldn't do X — extend your connection to unblock" row
     *  with a deep-link CTA. Optional for back-compat with pre-s236
     *  recap cards stored in feed_cards.payload jsonb. */
    blockers: z.array(recapBlockerSchema).max(4).optional(),
    /** One-sentence summary in mag's voice. Title of the card.
     *  ("i shipped 7 posts + paused 1 ad this week. anything off?") */
    summary: z.string().min(1).max(280),
});
//# sourceMappingURL=autopilot-recap.js.map